export type TomkinsonHandgripSex = 'M' | 'F';

export const TOMKINSON_HANDGRIP_2025_CITATION =
  'Tomkinson GR et al. International norms for adult handgrip strength. Journal of Sport and Health Science. 2025;14:101014. doi:10.1016/j.jshs.2024.101014';

export const TOMKINSON_HANDGRIP_2025_PERCENTILES = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95] as const;

export type TomkinsonHandgripPercentile = (typeof TOMKINSON_HANDGRIP_2025_PERCENTILES)[number];

export type TomkinsonHandgripCentilesByAge = {
  age: number;
  ageStart: number;
  ageEnd: number;
  values: Record<TomkinsonHandgripPercentile, number>;
};

type RawRow = [
  ageStart: number,
  ageEnd: number,
  p5: number,
  p10: number,
  p20: number,
  p30: number,
  p40: number,
  p50: number,
  p60: number,
  p70: number,
  p80: number,
  p90: number,
  p95: number,
];

const toCentileRows = (raw: RawRow[]): TomkinsonHandgripCentilesByAge[] =>
  raw.map((row) => {
    const [ageStart, ageEnd, ...vals] = row;
    const age = ageStart === 100 ? 100 : (ageStart + ageEnd) / 2;
    const values = TOMKINSON_HANDGRIP_2025_PERCENTILES.reduce((acc, p, idx) => {
      acc[p] = vals[idx]!;
      return acc;
    }, {} as Record<TomkinsonHandgripPercentile, number>);
    return { age, ageStart, ageEnd, values };
  });

const ABS_MALE_RAW: RawRow[] = [
  [20, 24, 33.9, 36.8, 40.5, 43.2, 45.7, 48.0, 50.4, 52.9, 56.0, 60.1, 63.6],
  [25, 29, 35.5, 38.5, 42.1, 44.8, 47.1, 49.3, 51.5, 53.9, 56.7, 60.7, 64.0],
  [30, 34, 35.0, 38.3, 42.2, 45.0, 47.4, 49.7, 52.0, 54.4, 57.4, 61.5, 64.9],
  [35, 39, 33.8, 37.3, 41.5, 44.5, 47.1, 49.5, 51.9, 54.4, 57.5, 61.8, 65.3],
  [40, 44, 32.3, 36.0, 40.4, 43.6, 46.3, 48.8, 51.2, 53.9, 57.1, 61.5, 65.1],
  [45, 49, 30.6, 34.4, 39.0, 42.3, 45.1, 47.6, 50.2, 52.9, 56.2, 60.7, 64.4],
  [50, 54, 28.9, 32.8, 37.4, 40.7, 43.5, 46.2, 48.8, 51.6, 54.8, 59.4, 63.1],
  [55, 59, 27.2, 31.0, 35.6, 38.9, 41.7, 44.4, 47.0, 49.8, 53.1, 57.7, 61.4],
  [60, 64, 25.5, 29.1, 33.6, 36.9, 39.7, 42.4, 45.0, 47.8, 51.1, 55.6, 59.3],
  [65, 69, 23.7, 27.2, 31.5, 34.7, 37.5, 40.1, 42.8, 45.6, 48.8, 53.2, 56.8],
  [70, 74, 21.9, 25.2, 29.3, 32.4, 35.1, 37.7, 40.3, 43.1, 46.3, 50.6, 54.1],
  [75, 79, 20.0, 23.1, 27.0, 29.9, 32.5, 35.1, 37.6, 40.3, 43.5, 47.7, 51.1],
  [80, 84, 18.0, 20.8, 24.5, 27.3, 29.8, 32.3, 34.8, 37.5, 40.5, 44.7, 48.0],
  [85, 89, 15.9, 18.5, 21.9, 24.6, 27.0, 29.4, 31.8, 34.4, 37.4, 41.5, 44.6],
  [90, 94, 13.7, 16.1, 19.2, 21.7, 24.0, 26.3, 28.7, 31.2, 34.2, 38.1, 41.2],
  [95, 99, 11.3, 13.5, 16.4, 18.8, 20.9, 23.1, 25.4, 27.9, 30.8, 34.6, 37.5],
  [100, 100, 8.8, 10.8, 13.5, 15.7, 17.8, 19.8, 22.0, 24.5, 27.2, 30.9, 33.8],
];

const ABS_FEMALE_RAW: RawRow[] = [
  [20, 24, 19.7, 21.7, 24.0, 25.7, 27.2, 28.6, 30.0, 31.6, 33.6, 36.6, 39.1],
  [25, 29, 20.0, 22.0, 24.5, 26.3, 27.9, 29.4, 30.9, 32.6, 34.6, 37.4, 39.7],
  [30, 34, 19.6, 21.8, 24.4, 26.4, 28.1, 29.7, 31.3, 33.1, 35.2, 38.0, 40.4],
  [35, 39, 19.0, 21.3, 24.1, 26.2, 28.0, 29.7, 31.4, 33.2, 35.4, 38.4, 40.8],
  [40, 44, 18.3, 20.7, 23.7, 25.8, 27.6, 29.4, 31.1, 33.0, 35.2, 38.3, 40.8],
  [45, 49, 17.6, 20.1, 23.1, 25.2, 27.1, 28.9, 30.6, 32.5, 34.8, 37.9, 40.4],
  [50, 54, 16.9, 19.4, 22.4, 24.5, 26.4, 28.2, 29.9, 31.8, 34.0, 37.1, 39.7],
  [55, 59, 16.1, 18.5, 21.5, 23.7, 25.5, 27.3, 29.0, 30.9, 33.0, 36.1, 38.6],
  [60, 64, 15.2, 17.6, 20.6, 22.7, 24.5, 26.2, 27.9, 29.7, 31.8, 34.9, 37.4],
  [65, 69, 14.3, 16.6, 19.5, 21.6, 23.3, 25.0, 26.6, 28.4, 30.5, 33.4, 35.8],
  [70, 74, 13.2, 15.5, 18.3, 20.3, 22.0, 23.6, 25.2, 26.9, 28.9, 31.8, 34.1],
  [75, 79, 12.0, 14.3, 17.0, 18.9, 20.5, 22.1, 23.6, 25.2, 27.2, 29.9, 32.2],
  [80, 84, 10.7, 12.9, 15.5, 17.4, 18.9, 20.4, 21.9, 23.5, 25.3, 28.0, 30.2],
  [85, 89, 9.3, 11.4, 13.9, 15.7, 17.2, 18.6, 20.0, 21.5, 23.3, 25.9, 28.0],
  [90, 94, 7.8, 9.8, 12.2, 13.9, 15.3, 16.7, 18.0, 19.5, 21.2, 23.6, 25.7],
  [95, 99, 6.1, 8.0, 10.3, 11.9, 13.3, 14.6, 15.9, 17.3, 18.9, 21.2, 23.2],
  [100, 100, 4.2, 6.1, 8.3, 9.8, 11.2, 12.4, 13.6, 14.9, 16.5, 18.7, 20.6],
];

const NORM_MALE_RAW: RawRow[] = [
  [20, 24, 11.0, 12.0, 13.2, 14.1, 14.8, 15.6, 16.3, 17.1, 18.0, 19.3, 20.4],
  [25, 29, 11.5, 12.5, 13.7, 14.6, 15.3, 16.1, 16.8, 17.6, 18.5, 19.8, 20.9],
  [30, 34, 11.6, 12.6, 13.8, 14.7, 15.5, 16.3, 17.0, 17.8, 18.7, 20.0, 21.1],
  [35, 39, 11.5, 12.5, 13.8, 14.7, 15.5, 16.2, 17.0, 17.8, 18.8, 20.1, 21.1],
  [40, 44, 11.2, 12.3, 13.6, 14.5, 15.3, 16.1, 16.9, 17.7, 18.6, 20.0, 21.0],
  [45, 49, 10.8, 11.9, 13.2, 14.2, 15.0, 15.8, 16.6, 17.4, 18.4, 19.7, 20.8],
  [50, 54, 10.4, 11.5, 12.8, 13.8, 14.6, 15.4, 16.2, 17.1, 18.0, 19.4, 20.5],
  [55, 59, 9.9, 11.0, 12.4, 13.3, 14.2, 15.0, 15.8, 16.6, 17.6, 19.0, 20.1],
  [60, 64, 9.3, 10.5, 11.8, 12.8, 13.6, 14.4, 15.2, 16.0, 17.0, 18.4, 19.6],
  [65, 69, 8.7, 9.9, 11.2, 12.2, 13.0, 13.8, 14.6, 15.4, 16.4, 17.8, 19.0],
  [70, 74, 8.1, 9.3, 10.6, 11.6, 12.4, 13.1, 13.8, 14.7, 15.6, 17.1, 18.3],
  [75, 79, 7.5, 8.6, 9.9, 10.8, 11.6, 12.3, 13.1, 13.8, 14.8, 16.2, 17.5],
  [80, 84, 6.9, 8.0, 9.2, 10.1, 10.8, 11.5, 12.2, 13.0, 13.9, 15.3, 16.6],
  [85, 89, 6.2, 7.3, 8.5, 9.3, 10.0, 10.6, 11.2, 12.0, 12.9, 14.3, 15.6],
  [90, 94, 5.5, 6.5, 7.7, 8.4, 9.1, 9.6, 10.2, 10.9, 11.8, 13.2, 14.6],
  [95, 99, 4.8, 5.7, 6.8, 7.5, 8.1, 8.6, 9.2, 9.8, 10.7, 12.1, 13.4],
  [100, 100, 4.0, 4.9, 5.9, 6.5, 7.1, 7.5, 8.0, 8.6, 9.4, 10.8, 12.2],
];

const NORM_FEMALE_RAW: RawRow[] = [
  [20, 24, 7.4, 8.1, 9.0, 9.7, 10.3, 10.8, 11.4, 12.0, 12.7, 13.8, 14.7],
  [25, 29, 7.6, 8.3, 9.3, 10.0, 10.6, 11.1, 11.7, 12.4, 13.1, 14.2, 15.0],
  [30, 34, 7.6, 8.4, 9.3, 10.1, 10.7, 11.3, 11.9, 12.5, 13.3, 14.4, 15.2],
  [35, 39, 7.5, 8.3, 9.3, 10.0, 10.7, 11.3, 11.9, 12.6, 13.4, 14.4, 15.3],
  [40, 44, 7.3, 8.2, 9.2, 10.0, 10.6, 11.2, 11.9, 12.5, 13.3, 14.4, 15.3],
  [45, 49, 7.2, 8.0, 9.1, 9.8, 10.5, 11.1, 11.7, 12.4, 13.2, 14.3, 15.2],
  [50, 54, 7.0, 7.8, 8.9, 9.6, 10.3, 10.9, 11.5, 12.2, 13.0, 14.1, 15.0],
  [55, 59, 6.7, 7.6, 8.6, 9.4, 10.1, 10.7, 11.3, 12.0, 12.7, 13.8, 14.7],
  [60, 64, 6.4, 7.3, 8.3, 9.1, 9.7, 10.4, 11.0, 11.6, 12.4, 13.5, 14.4],
  [65, 69, 6.1, 6.9, 8.0, 8.7, 9.4, 10.0, 10.6, 11.2, 12.0, 13.0, 14.0],
  [70, 74, 5.7, 6.5, 7.6, 8.3, 8.9, 9.5, 10.1, 10.7, 11.5, 12.5, 13.4],
  [75, 79, 5.2, 6.1, 7.1, 7.8, 8.4, 9.0, 9.5, 10.1, 10.9, 11.9, 12.8],
  [80, 84, 4.6, 5.5, 6.6, 7.3, 7.9, 8.4, 8.9, 9.5, 10.2, 11.2, 12.1],
  [85, 89, 3.9, 4.9, 5.9, 6.6, 7.2, 7.7, 8.2, 8.8, 9.4, 10.5, 11.4],
  [90, 94, 3.1, 4.1, 5.2, 5.9, 6.5, 6.9, 7.4, 7.9, 8.6, 9.6, 10.5],
  [95, 99, 2.2, 3.3, 4.4, 5.1, 5.6, 6.1, 6.5, 7.0, 7.7, 8.7, 9.6],
  [100, 100, 1.1, 2.3, 3.5, 4.2, 4.7, 5.1, 5.5, 6.0, 6.6, 7.6, 8.6],
];

export const TOMKINSON_HANDGRIP_2025_ABSOLUTE_KG: Record<TomkinsonHandgripSex, TomkinsonHandgripCentilesByAge[]> = {
  M: toCentileRows(ABS_MALE_RAW),
  F: toCentileRows(ABS_FEMALE_RAW),
};

export const TOMKINSON_HANDGRIP_2025_NORMALIZED_KG_PER_M2: Record<TomkinsonHandgripSex, TomkinsonHandgripCentilesByAge[]> = {
  M: toCentileRows(NORM_MALE_RAW),
  F: toCentileRows(NORM_FEMALE_RAW),
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export const getTomkinsonCentilesForAgeBand = (
  rows: TomkinsonHandgripCentilesByAge[],
  ageYears: number,
): Record<TomkinsonHandgripPercentile, number> | null => {
  if (!rows.length) return null;

  const minimumAge = rows[0]?.ageStart ?? 20;
  const maximumAge = rows[rows.length - 1]?.ageEnd ?? 100;
  const age = clamp(ageYears, minimumAge, maximumAge);
  const matchingRow = rows.find((row) => age >= row.ageStart && age <= row.ageEnd);

  return matchingRow?.values ?? null;
};

export const estimatePercentileFromTomkinsonCentiles = (
  centiles: Record<TomkinsonHandgripPercentile, number>,
  value: number,
) => {
  const ps = [...TOMKINSON_HANDGRIP_2025_PERCENTILES];
  const vs = ps.map((p) => centiles[p]);

  if (value <= vs[0]) {
    const p0 = ps[0];
    const p1 = ps[1];
    const v0 = vs[0];
    const v1 = vs[1];
    const t = v1 !== v0 ? (value - v0) / (v1 - v0) : 0;
    return clamp(p0 + (p1 - p0) * t, 0, 100);
  }

  if (value >= vs[vs.length - 1]) {
    const p0 = ps[ps.length - 2];
    const p1 = ps[ps.length - 1];
    const v0 = vs[vs.length - 2];
    const v1 = vs[vs.length - 1];
    const t = v1 !== v0 ? (value - v0) / (v1 - v0) : 0;
    return clamp(p0 + (p1 - p0) * t, 0, 100);
  }

  for (let i = 0; i < ps.length - 1; i += 1) {
    const p0 = ps[i];
    const p1 = ps[i + 1];
    const v0 = vs[i];
    const v1 = vs[i + 1];
    const lo = Math.min(v0, v1);
    const hi = Math.max(v0, v1);
    if (value < lo || value > hi) continue;
    const t = v1 !== v0 ? (value - v0) / (v1 - v0) : 0;
    return clamp(p0 + (p1 - p0) * t, 0, 100);
  }

  return null;
};
